import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="pt-24 pb-20 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-2 gradient-text">Privacy Policy</h1>
          <p className="text-sm text-slate-500 mb-10">Last updated: 31 May 2026</p>

          <div className="glass-card p-8 space-y-8 text-slate-300 leading-relaxed">
            <section>
              <h2 className="text-xl font-bold text-white mb-3">Summary</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>The Evaluator runs in your browser. Pasted SKILL.md content is not stored on our servers when scored client-side.</li>
                <li>When you trigger a security scan, the SKILL.md or GitHub URL is sent to our internal scanner for analysis and is not retained after the scan completes.</li>
                <li>When you submit a skill, its metadata and content are stored in the directory and shown publicly.</li>
                <li>Authentication uses GitHub OAuth or email/password. We store the minimum needed to identify your account.</li>
                <li>We do not sell your data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">1. What We Collect</h2>
              <p><strong>Account data:</strong> username, email, hashed password (if you register with email), GitHub user ID + avatar (if you sign in with GitHub), display name, optional bio.</p>
              <p className="mt-3"><strong>Submitted skill data:</strong> the GitHub repository URL, the SKILL.md content, README content, frontmatter, categories, tags, compatible-agent list, install counters, the quality score and security report we generate, and timestamps.</p>
              <p className="mt-3"><strong>Telemetry:</strong> server logs (IP address, request path, status code, user agent, response time). Logs are retained for up to 30 days for debugging and rate-limiting purposes.</p>
              <p className="mt-3"><strong>Cookies:</strong> a single HTTP-only authentication cookie when you sign in. No third-party advertising cookies. No cross-site tracking.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">2. What We Do With It</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Operate the directory, evaluator, and security scanner.</li>
                <li>Score submitted skills (quality + security) so visitors can make informed decisions.</li>
                <li>Show install counts, leaderboards, and trending sections.</li>
                <li>Detect and block abuse (per-IP rate limits, daily scan budgets).</li>
                <li>Communicate with you about your account (account-related email only).</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">3. Evaluator and Scanner</h2>
              <p>The quality score is computed by JavaScript that runs in your browser. The pasted SKILL.md content stays on your device for client-side scoring.</p>
              <p className="mt-3">The security scan runs on our infrastructure. The SKILL.md content or GitHub URL you provide is sent to an internal scanner service. We do not retain the submitted content after the scan completes. If you submit a skill to the directory, the SKILL.md is stored as part of the submission (see section 1).</p>
              <p className="mt-3">If the optional LLM analysis stage is enabled, scan content is sent to an LLM provider (currently Anthropic) for semantic analysis. The provider's privacy terms apply to that processing. We do not authorize the provider to train on your content.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">4. Third-Party Services</h2>
              <p>We rely on the following:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>GitHub (OAuth, public-repo fetch).</li>
                <li>Amazon Web Services (hosting, storage, secret management).</li>
                <li>Anthropic API (optional LLM stage of the security scan).</li>
                <li>MongoDB Atlas (database hosting).</li>
              </ul>
              <p className="mt-3">Each has its own privacy policy.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">5. Public Data</h2>
              <p>Submitted skills are public by design: name, description, SKILL.md content, README, scores, install counts, and the publishing username are visible to anyone who visits the directory.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">6. Your Choices</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Delete your account:</strong> email <a className="text-brand-300" href="mailto:support@strongly.ai">support@strongly.ai</a> and we will remove your account and unpublish your submissions.</li>
                <li><strong>Unpublish a skill:</strong> delete the underlying GitHub repository or contact us.</li>
                <li><strong>Decline cookies:</strong> sign-in requires the auth cookie; without it, anonymous browsing is fine.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">7. Security</h2>
              <p>Account passwords are hashed with bcrypt. The Anthropic API key used by the scanner is stored encrypted in AWS SSM Parameter Store and is never written to a plaintext file on disk. The scanner sidecar runs in a hardened container with read-only filesystem and dropped Linux capabilities, accessible only from our main application security group.</p>
              <p className="mt-3">No system is perfectly secure. By using the Site, you accept that residual risk.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">8. Children</h2>
              <p>The Site is not directed at children under 13 and we do not knowingly collect data from them.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">9. Changes</h2>
              <p>We may update this Policy. Material changes will be announced on the Site. Continued use means acceptance.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-white mb-3">10. Contact</h2>
              <p>Privacy questions: <a className="text-brand-300" href="mailto:support@strongly.ai">support@strongly.ai</a></p>
            </section>

            <p className="text-xs text-slate-500 pt-4 border-t border-white/5">
              See also: <Link to="/terms" className="text-brand-300 hover:text-brand-200">Terms of Service</Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
